import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Users, Crown, Plus, Trash2, LogOut, ShieldCheck } from "lucide-react";

export const TeamPage = () => {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/teams/me")
      .then((r) => setTeam(r.data))
      .catch(() => setTeam(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-10 text-slate-400">Loading team…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="mono-label">recruiter workspace</div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
        {team ? team.name : "Team accounts"}
      </h1>
      <p className="text-slate-400 mt-2">
        Bring your co-recruiters into the same workspace — everyone sees the same
        jobs, applicants, invites, and analytics.
      </p>

      {!team ? <CreateTeamCard onCreated={load} /> : <TeamCard team={team} onChanged={load} />}
    </div>
  );
};

const CreateTeamCard = ({ onCreated }) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!name.trim()) return toast.error("Give your team a name");
    setSaving(true);
    try {
      await api.post("/teams", { name: name.trim() });
      toast.success("Team created");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create team");
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-8 border border-white/10 bg-[#111827] rounded-xl p-6" data-testid="create-team-card">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-[#06B6D4]" />
        <div className="mono-label">create a team</div>
      </div>
      <div className="text-sm text-slate-400 mb-4">
        As the owner you'll be able to invite other recruiters by email. Every member
        gets full access to shared jobs, applicants, and invite history.
      </div>
      <div className="flex flex-col md:flex-row gap-3">
        <Input
          placeholder="Team name (e.g. Northwind Recruiting)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="team-name-input"
          className="bg-[#0B0F19] border-white/10 flex-1"
        />
        <Button
          onClick={create}
          disabled={saving}
          data-testid="create-team-btn"
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-6"
        >
          {saving ? "Creating…" : "Create team"}
        </Button>
      </div>
    </div>
  );
};

const TeamCard = ({ team, onChanged }) => {
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const isOwner = team.my_role === "owner";

  const add = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try {
      await api.post("/teams/members", { email: email.trim().toLowerCase() });
      setEmail("");
      toast.success("Member added");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add member");
    } finally { setAdding(false); }
  };

  const remove = async (userId) => {
    if (!confirm("Remove this member from the team?")) return;
    try {
      await api.delete(`/teams/members/${userId}`);
      toast.success("Member removed");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove member");
    }
  };

  const leave = async () => {
    if (!confirm("Leave the team? You'll lose access to shared jobs and analytics.")) return;
    try {
      await api.post("/teams/leave");
      toast.success("Left the team");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not leave");
    }
  };

  return (
    <div className="mt-8 space-y-4" data-testid="team-card">
      <div className="border border-white/10 bg-[#111827] rounded-xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="mono-label flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-[#06B6D4]" />
              your role · {team.my_role}
            </div>
            <div className="text-sm text-slate-400 mt-2">
              {team.members.length} member{team.members.length === 1 ? "" : "s"} share this workspace
            </div>
          </div>
          <Button
            variant="outline"
            onClick={leave}
            data-testid="team-leave-btn"
            className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white rounded-full"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> {isOwner ? "Dissolve or leave" : "Leave team"}
          </Button>
        </div>
      </div>

      {isOwner && (
        <div className="border border-white/10 bg-[#111827] rounded-xl p-6">
          <div className="mono-label mb-3 flex items-center gap-1.5">
            <Plus className="w-3 h-3" /> add a member
          </div>
          <div className="text-xs text-slate-500 mb-3">
            They must already have a recruiter account on Axiony. Ask them to sign
            up first, then paste their email here.
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <Input
              placeholder="recruiter@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="team-add-email"
              className="bg-[#0B0F19] border-white/10 flex-1"
            />
            <Button
              onClick={add}
              disabled={adding}
              data-testid="team-add-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-6"
            >
              {adding ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>
      )}

      <div className="border border-white/10 bg-[#111827] rounded-xl p-6">
        <div className="mono-label mb-3">members</div>
        <div className="space-y-1">
          {team.members.map((m) => (
            <div
              key={m.user_id}
              data-testid={`team-member-${m.user_id}`}
              className="flex items-center justify-between gap-3 border border-white/5 rounded-lg px-3 py-2"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Avatar className="w-8 h-8 border border-white/10">
                  <AvatarFallback className="bg-[#1F2937] text-slate-300 text-[10px]">
                    {(m.name || m.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-sm text-slate-200 truncate flex items-center gap-2">
                    {m.name || m.email}
                    {m.role === "owner" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#F59E0B]/40 text-[#F59E0B] font-mono uppercase flex items-center gap-1">
                        <Crown className="w-2.5 h-2.5" /> owner
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                </div>
              </div>
              {isOwner && m.role !== "owner" && (
                <button
                  type="button"
                  onClick={() => remove(m.user_id)}
                  data-testid={`team-remove-${m.user_id}`}
                  className="text-slate-500 hover:text-red-400 p-1.5"
                  title="Remove from team"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
